import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API });

export const api = {
  getMetrics: () => client.get("/dashboard/metrics").then((r) => r.data),
  getNeedsAttention: () => client.get("/dashboard/needs-attention").then((r) => r.data),
  getImpact: () => client.get("/impact").then((r) => r.data),
  listClients: () => client.get("/clients").then((r) => r.data),
  getClient: (slug) => client.get(`/clients/${slug}`).then((r) => r.data),
  listCommitments: (params = {}) => client.get("/commitments", { params }).then((r) => r.data),
  getCommitment: (id) => client.get(`/commitments/${id}`).then((r) => r.data),
  updateCommitment: (id, body) => client.patch(`/commitments/${id}`, body).then((r) => r.data),
  fulfillWithEvidence: (id, body) => client.post(`/commitments/${id}/fulfill`, body).then((r) => r.data),
  analyze: (payload) => client.post("/analyze", payload).then((r) => r.data),
  accept: (commitments) => client.post("/commitments/accept", { commitments }).then((r) => r.data),
  followUp: (id) => client.post(`/commitments/${id}/follow-up`).then((r) => r.data),
  reconcile: (id, body) => client.post(`/commitments/${id}/reconcile`, body).then((r) => r.data),
  addCalendar: (id, payload) => client.post(`/commitments/${id}/calendar-event`, payload).then((r) => r.data),
  icsUrl: (id) => `${API}/commitments/${id}/ics`,
  icsBatchUrl: () => `${API}/commitments/ics-batch`,
  gmailThreads: (window) => client.get("/gmail/threads", { params: window ? { window } : {} }).then((r) => r.data),
  gmailAnalyze: (tid) => client.post(`/gmail/analyze/${tid}`).then((r) => r.data),
  gmailAnalyzeWindow: (window) => client.post("/gmail/analyze-window", { window }).then((r) => r.data),
  seed: () => client.post("/seed").then((r) => r.data),
  reset: () => client.post("/demo/reset").then((r) => r.data),
  demoStatus: () => client.get("/demo/status").then((r) => r.data),
  requestPilot: (payload) => client.post("/pilot-requests", payload).then((r) => r.data),
};

const todayIso = () => new Date().toISOString().slice(0, 10);

export const isOverdue = (c) => {
  if (!["open", "partially_fulfilled", "blocked"].includes(c.status)) return false;
  if (!c.expected_completion) return false;
  return c.expected_completion < todayIso();
};
export const isDueToday = (c) => ["open", "partially_fulfilled"].includes(c.status) && c.expected_completion === todayIso();
export const isDueSoon = (c) => {
  if (!["open", "partially_fulfilled"].includes(c.status) || !c.expected_completion) return false;
  const t = new Date(); const d = new Date(c.expected_completion);
  const diff = (d - t) / (1000 * 60 * 60 * 24);
  return diff >= 0 && diff <= 7;
};

export const stateOf = (c) => {
  if (c.status === "fulfilled") return "fulfilled";
  if (c.status === "dismissed") return "dismissed";
  if (c.status === "cancelled") return "cancelled";
  if (c.status === "conditional") return "conditional";
  if (c.status === "blocked") return "blocked";
  if (c.status === "partially_fulfilled") return "partially_fulfilled";
  if (c.status === "needs_clarification") return "needs_clarification";
  if (isOverdue(c)) return "overdue";
  if (isDueToday(c)) return "due_today";
  if (isDueSoon(c)) return "due_soon";
  return "open";
};

export const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};
export const formatMoney = (n, currency = "USD") => {
  if (n == null) return null;
  const sym = currency === "USD" ? "$" : "";
  return sym + Math.round(n).toLocaleString("en-US");
};

export const relativeDue = (iso) => {
  if (!iso) return "no due date";
  const today = new Date(); today.setHours(0,0,0,0);
  const due = new Date(iso); due.setHours(0,0,0,0);
  const diff = Math.round((due - today) / 86400000);
  if (diff === 0) return "due today";
  if (diff === 1) return "due tomorrow";
  if (diff === -1) return "1 day overdue";
  if (diff < 0) return `${-diff} days overdue`;
  return `due in ${diff} days`;
};

export const fulfilledLabel = (c) => {
  if (c.fulfilled_label) return c.fulfilled_label;
  if (c.status !== "fulfilled") return null;
  if (!c.fulfilled_at || !c.expected_completion) return "Fulfilled";
  const f = new Date(c.fulfilled_at.slice(0, 10));
  const e = new Date(c.expected_completion);
  const diff = Math.round((f - e) / 86400000);
  if (diff < 0) return `Fulfilled ${-diff} day${-diff !== 1 ? "s" : ""} early`;
  if (diff === 0) return "Fulfilled on time";
  return `Fulfilled ${diff} day${diff !== 1 ? "s" : ""} late`;
};

export const stateMeta = {
  open: { bg: "#DBEAFE", fg: "#1D4ED8", label: "Open" },
  due_today: { bg: "#FEF3C7", fg: "#92400E", label: "Due Today" },
  due_soon: { bg: "#FEF3C7", fg: "#92400E", label: "Due Soon" },
  overdue: { bg: "#FEE2E2", fg: "#B91C1C", label: "Overdue" },
  fulfilled: { bg: "#D1FAE5", fg: "#047857", label: "Fulfilled" },
  dismissed: { bg: "#F5F5F5", fg: "#525252", label: "Dismissed" },
  conditional: { bg: "#EDE9FE", fg: "#6D28D9", label: "Conditional" },
  needs_clarification: { bg: "#F5F5F5", fg: "#525252", label: "Needs Clarification" },
  blocked: { bg: "#FEE2E2", fg: "#991B1B", label: "Blocked" },
  partially_fulfilled: { bg: "#DBEAFE", fg: "#065F46", label: "Partially Fulfilled" },
  cancelled: { bg: "#F5F5F5", fg: "#525252", label: "Cancelled" },
};

export const reconciliationMeta = {
  fulfilled: { bg: "#F0FDF4", border: "#10B981", fg: "#047857", label: "Possible fulfilment detected" },
  partially_fulfilled: { bg: "#EFF6FF", border: "#3B82F6", fg: "#1D4ED8", label: "Possible partial fulfilment detected" },
  revised: { bg: "#FFFBEB", border: "#F59E0B", fg: "#92400E", label: "Possible revision detected" },
  delayed: { bg: "#FFFBEB", border: "#F59E0B", fg: "#92400E", label: "Possible delay detected" },
  blocked: { bg: "#FEF2F2", border: "#EF4444", fg: "#991B1B", label: "Possible block detected" },
  cancelled: { bg: "#F5F5F5", border: "#525252", fg: "#525252", label: "Possible cancellation detected" },
  condition_satisfied: { bg: "#F0FDF4", border: "#10B981", fg: "#047857", label: "Possible condition satisfied" },
  condition_failed: { bg: "#FEF2F2", border: "#EF4444", fg: "#991B1B", label: "Possible failed condition" },
};

export const isStaleReviewError = (err) => {
  const d = err?.response?.data?.detail;
  if (!d) return false;
  if (typeof d === "object" && d.error_code === "STALE_COMMITMENT_REVIEW") return true;
  return false;
};

export const staleReviewPayload = (err) => err?.response?.data?.detail || null;

export const strengthMeta = {
  strong: { bg: "#F5F5F5", fg: "#171717", label: "Strong" },
  soft: { bg: "#FEF3C7", fg: "#92400E", label: "Soft" },
  conditional: { bg: "#EDE9FE", fg: "#6D28D9", label: "Conditional" },
};
